import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider, createBrowserHistory } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { type Root, createRoot } from 'react-dom/client';

import { initI18n } from '@/lib/i18n';
import {
  CACHE_BUSTER,
  MAX_AGE_MS,
  createIdbPersister,
  createQueryClient,
} from '@/lib/offline/persister';

import { ClientDepsProvider, createClientDeps } from './deps';
import { createClientRouter } from './router';

/**
 * The client app's bootstrap. `main.tsx` stays a two-line entry; everything
 * testable lives here.
 *
 * Returns the root so a caller can dispose it. A test that leaves one mounted
 * keeps React scheduling work that flushes after the jsdom environment is torn
 * down, which surfaces as `ReferenceError: window is not defined` and fails the
 * whole vitest run on an unhandled error.
 */
export function mount(container: Element | null): Root {
  if (container === null) {
    throw new Error('26AD client: #root container is missing from index.html');
  }
  initI18n();

  const root = createRoot(container);
  root.render(
    <StrictMode>
      <PersistQueryClientProvider
        client={createQueryClient()}
        persistOptions={{
          persister: createIdbPersister(indexedDB),
          maxAge: MAX_AGE_MS,
          buster: CACHE_BUSTER,
        }}
      >
        <ClientDepsProvider deps={createClientDeps()}>
          <RouterProvider router={createClientRouter(createBrowserHistory())} />
        </ClientDepsProvider>
      </PersistQueryClientProvider>
    </StrictMode>,
  );
  return root;
}
